/**
 * The orchestrator-facing face of the D29 shape ladder: translate rung
 * results into clause outcomes, drive the polite-request retry, and stamp the
 * D28 language evidence — extracted from `parse-command` so the pipeline file
 * stays within the repo's decomposition cap.
 *
 * @remarks
 * The polite-request rung re-enters the REAL pipeline through an injected
 * callback (the same inversion the D18/D21 rescues use), so this module
 * needs no knowledge of the clause walk it serves. When the courtesy retry
 * also fails, the ladder runs once more with the request rung disabled so the
 * clause still receives its question/statement register instead of a raw
 * diagnostic.
 *
 * @packageDocumentation
 */

import { type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import { BUILTIN_PRONOUN_TOKENS, SOFT_FILLER_WORDS } from '../lexicon/builtin-function-words.js';
import { type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { type ClauseParseResult } from '../rescue/parse-guided-rescues.js';
import {
	type EntityRef,
	type ParseCandidateEntity,
	type ParseDiagnostics,
	type Token,
	type TokenCorrection,
} from '../types.js';
import { looksLikeKeyboardMash } from './mash-evidence.js';
import { rescueBareNoun } from './rescue-bare-noun.js';
import { rescueSentenceShape, SHAPE_ROUTED_CONFIDENCE, type ShapeRescueResult } from './rescue-sentence-shape.js';

/** Everything one ladder application needs (the reparse hook carries the pipeline). */
export interface ShapeLadderInput {
	/** The clause's canonical tokens, fuzzy-corrected — what the bare-noun rung resolves (D133). */
	readonly canonicalTokens: readonly CanonicalToken[];
	/** The clause, folded (clarifications and routed commands cite it). */
	readonly clauseText: string;
	/** The clause's tokens as parsed (soft filler already stripped). */
	readonly clauseTokens: readonly Token[];
	/** Corrections applied so far — they ride onto routed commands. */
	readonly corrections: readonly TokenCorrection[];
	/** The per-parse catalog. */
	readonly entities: readonly ParseCandidateEntity[];
	/** The compiled vocabulary (word classes + declared intents). */
	readonly lexicon: CompiledLexicon;
	/** Most-recent-first refs, for a bare pronoun (D67 through the bare-noun rung). */
	readonly recentEntityRefs: readonly EntityRef[];
	/** Re-run the REAL pipeline on the courtesy-stripped remainder. */
	readonly reparseWithoutCourtesyPrefix: (strippedTokens: readonly Token[]) => ClauseParseResult;
}

/**
 * Run the D29 shape ladder for one grammar-beaten clause.
 *
 * @param input - The ladder's inputs (see {@link ShapeLadderInput})
 * @returns The shape-claimed outcome, or `undefined` when no shape spoke
 */
export const applyShapeLadder = (input: ShapeLadderInput): ClauseParseResult | undefined => {
	const ladderOptions = { canonicalTokens: input.canonicalTokens, recentEntityRefs: input.recentEntityRefs };
	const firstPass = rescueSentenceShape(input.clauseTokens, input.clauseText, input.lexicon, input.entities, ladderOptions);

	if (firstPass?.type === 'reparse_as_request') {
		const retry = input.reparseWithoutCourtesyPrefix(firstPass.strippedTokens);

		if (retry.type === 'command' || retry.type === 'clarification') {
			return retry;
		}

		// The courtesy reading failed too — rerun the ladder, request rung off.
		return translateShapeResult(
			rescueSentenceShape(input.clauseTokens, input.clauseText, input.lexicon, input.entities, {
				...ladderOptions,
				allowRequestRung: false,
			}),
			input,
		);
	}

	return translateShapeResult(firstPass, input);
};

/**
 * D133: read one clause as a NOUN PHRASE ahead of the grammar
 * (`ParserOptions.readAsNounPhrase`) — the bare-noun rung alone, translated
 * exactly as the ladder would translate it. An answer slot asks this: the
 * question supplied the verb, so "pry bar" is the bar's name, not `pry`
 * aimed at it (D64).
 *
 * @param input - The ladder's inputs (the reparse hook is unused here)
 * @returns The routed look or the which-entity question, or `undefined` when the clause is not one thing's name
 */
export const readClauseAsNounPhrase = (input: ShapeLadderInput): ClauseParseResult | undefined =>
	translateShapeResult(
		rescueBareNoun(
			input.canonicalTokens,
			input.clauseText,
			input.lexicon,
			input.entities,
			input.recentEntityRefs,
			'before_the_grammar',
		),
		input,
	);

/**
 * D28/D31: stamp `resemblesLanguage` onto a `no_intent_recognized`
 * diagnostic. The burden of proof runs TOWARD the player (D31 inversion,
 * Calder's brief): any recognized token → language; else any token with
 * positive mash shape ({@link looksLikeKeyboardMash}) → not language; else —
 * unknown but word-shaped — language, benefit of the doubt. Register evidence
 * for the consumer's failure voice; never affects resolution.
 *
 * @param diagnostics - The failure diagnostics as built
 * @param clauseTokens - The failing clause's original tokens (stopwords included)
 * @param lexicon - The compiled vocabulary (word sets)
 * @param legalWordSet - The parse call's legal words (vocabulary + catalog)
 * @returns The diagnostics, enriched when the failure kind warrants it
 */
export const withLanguageEvidence = (
	diagnostics: ParseDiagnostics,
	clauseTokens: readonly Token[],
	lexicon: CompiledLexicon,
	legalWordSet: ReadonlySet<string>,
): ParseDiagnostics => {
	if (diagnostics.failureKind !== 'no_intent_recognized') {
		return diagnostics;
	}

	const pronounWords: readonly string[] = BUILTIN_PRONOUN_TOKENS;
	const hasRecognizedToken = clauseTokens.some(
		(clauseToken) =>
			legalWordSet.has(clauseToken.text) ||
			lexicon.stopwordSet.has(clauseToken.text) ||
			lexicon.recognitionWordSet.has(clauseToken.text) ||
			SOFT_FILLER_WORDS.has(clauseToken.text) ||
			pronounWords.includes(clauseToken.text),
	);
	const hasMashShapedToken = clauseTokens.some((clauseToken) => looksLikeKeyboardMash(clauseToken.text));

	return { ...diagnostics, resemblesLanguage: hasRecognizedToken || !hasMashShapedToken };
};

/**
 * Translate one rung result into a clause outcome (routed questions become
 * hand-assembled commands; the vocabulary's compiled intent supplies the
 * branded token — shape routing invents nothing).
 *
 * @param shapeResult - The rung's product
 * @param input - The ladder's inputs
 * @returns The clause outcome, or `undefined` when nothing translated
 */
const translateShapeResult = (
	shapeResult: ShapeRescueResult | undefined,
	input: ShapeLadderInput,
): ClauseParseResult | undefined => {
	if (shapeResult === undefined || shapeResult.type === 'reparse_as_request') {
		return undefined;
	}

	if (shapeResult.type === 'clarification' || shapeResult.type === 'diagnostics') {
		return shapeResult;
	}

	if (shapeResult.type === 'routed_movement') {
		return {
			command: {
				clauseText: input.clauseText,
				confidence: SHAPE_ROUTED_CONFIDENCE,
				corrections: input.corrections,
				direction: shapeResult.direction,
				// D231: the thing the chatter named, as typed — the consumer decides by it.
				directObjectRef: shapeResult.destination.entityRef,
				directObjectSurface: shapeResult.destination.matchedTokens.join(' '),
				intent: input.lexicon.movementIntent.canonicalToken,
				intentSurface: input.lexicon.movementIntent.canonicalToken,
				qualifiers: [],
				rescuedAs: 'direction_with_chatter',
			},
			type: 'command',
		};
	}

	// D133: the bare noun is a look at the thing it names, and the command says
	// so (`rescuedAs`) — the runtime's D37 follow-up keys on that rather than
	// re-parsing the text to find out what the player did.
	if (shapeResult.type === 'routed_bare_noun') {
		const { intent, resolution } = shapeResult.routed;

		return {
			command: {
				clauseText: input.clauseText,
				confidence: SHAPE_ROUTED_CONFIDENCE,
				corrections: input.corrections,
				directObjectRef: resolution.entityRef,
				directObjectSurface: resolution.headSurface,
				intent: intent.canonicalToken,
				intentSurface: intent.canonicalToken,
				...(resolution.pronounResolution === undefined ? {} : { pronounResolutions: [resolution.pronounResolution]}),
				qualifiers: resolution.qualifiers,
				rescuedAs: 'bare_noun',
			},
			type: 'command',
		};
	}

	// The rung already resolved the intent against this very lexicon before it
	// agreed to route (`rescueQuestion` routes nothing the vocabulary does not
	// declare), and it hands the record over on the result — so there is no
	// second lookup here, and no undefined-guard on one that could never miss.
	const { routed } = shapeResult;

	return {
		command: {
			clauseText: input.clauseText,
			confidence: SHAPE_ROUTED_CONFIDENCE,
			corrections: input.corrections,
			intent: routed.intent.canonicalToken,
			intentSurface: routed.intent.canonicalToken,
			qualifiers: [],
			rescuedAs: 'question',
			...(routed.bindsAs === 'indirect_object'
				? { indirectObjectRef: routed.entityRef }
				: { directObjectRef: routed.entityRef }),
		},
		type: 'command',
	};
};
